import React, { Suspense, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LOCALES, type LocaleCode } from "@/i18n/locales";
import { applyDocumentLocale, normalizeLocale } from "@/i18n";
import { safeStorage } from "@/utils/storage";
import { APP_ROUTES } from "./routes";
import { StubPage } from "@/pages/stub/stub-page";
import { LoadingState } from "@/pages/shared/feedback";
import "./app-router.css";

/** Sends unprefixed and unknown URLs to the last valid locale. */
function LocaleRedirect() {
    const stored = safeStorage.getItem("pitango-locale");
    const locale = normalizeLocale(stored);
    return <Navigate to={`/${locale}`} replace />;
}

/**
 * Synchronizes a validated `:locale` route segment with i18next and the HTML
 * `lang`/`dir` attributes. Every user-facing route must use this boundary.
 */
function LocaleBoundary({ children }: { children: React.ReactNode }) {
    const params = useParams<{ locale: string }>();
    const location = useLocation();
    const { i18n } = useTranslation();
    const previousLocaleRef = useRef<string | undefined>(undefined);
    const [isLocaleChanging, setIsLocaleChanging] = useState(false);
    const rawLocale = params.locale;
    const valid = Boolean(rawLocale && rawLocale in LOCALES);
    const locale = valid ? rawLocale as LocaleCode : "en";

    useEffect(() => {
        if (!valid) return;
        if (i18n.language !== locale) void i18n.changeLanguage(locale);
        applyDocumentLocale(locale);
    }, [i18n, locale, valid]);

    useLayoutEffect(() => {
        if (!valid) return;
        if (previousLocaleRef.current === undefined) {
            previousLocaleRef.current = locale;
            return;
        }
        if (previousLocaleRef.current === locale) return;

        previousLocaleRef.current = locale;
        setIsLocaleChanging(true);
        const timer = window.setTimeout(() => setIsLocaleChanging(false), 360);
        return () => window.clearTimeout(timer);
    }, [locale, valid]);

    if (!valid) return <Navigate to="/en" replace />;
    const pageKey = `${location.pathname}${location.search}${location.hash}`;
    return (
        <div className={`locale-page${isLocaleChanging ? " locale-page--changing" : ""}`}>
            <div key={pageKey} className="page-transition">
                {children}
            </div>
        </div>
    );
}

/** Locale-prefixed route registry. Add new pages under `/:locale/...`. */
export function AppRouter() {
    return (
        <Routes>
            <Route path="/" element={<LocaleRedirect />} />
            {APP_ROUTES.map(route => (
                <Route
                    key={route.path || "index"}
                    path={route.path ? `/:locale/${route.path}` : "/:locale"}
                    element={<LocaleBoundary><Suspense fallback={<LoadingState />}>{route.element}</Suspense></LocaleBoundary>}
                />
            ))}
            <Route path="/:locale/*" element={<LocaleBoundary><StubPage titleKey="stub.pages.notFound" /></LocaleBoundary>} />
            <Route path="*" element={<LocaleRedirect />} />
        </Routes>
    );
}
